import './EditableTableCell.scss';
import React, { useEffect, useState } from 'react';
import { Input } from 'reactstrap';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition as IDSvg } from '@fortawesome/free-regular-svg-icons';
import {
    faXmark,
    faFloppyDisk,
    faPenToSquare,
    IconDefinition as IDSolid,
    faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import PortalTooltip from '../../../components/PortalTooltip/PortalTooltip';

interface EditableTableCellProps {
    cellViewContent?: JSX.Element;
    cellValue: string;
    editIcon?: IDSolid | IDSvg;
    editIconSize?: SizeProp;
    saveIcon?: IDSolid | IDSvg;
    saveIconSize?: SizeProp;
    cancelIcon?: IDSolid | IDSvg;
    cancelIconSize?: SizeProp;
    updateCellValue: (newValue: string) => boolean;
    cellValidator?: (value: string) => boolean;
    id?: string;
    isDeleteAvailable?: boolean;
    deleteIcon?: IDSolid | IDSvg;
    deleteIconSize?: SizeProp;
    deleteIconClasses?: string;
    onDeleteClick?(): void;
    onTextClick?(): void;
}

const EditableTableCell = (props: EditableTableCellProps) => {
    const [editorState, setEditorState] = useState<'view' | 'edit'>('view');
    const [hasError, setHasError] = useState(false);
    const [cellValue, setCellValue] = useState<string>(props.cellValue);

    const validateCellValue = props.cellValidator
        ? props.cellValidator
        : (name: string) => String(name).trim().length > 0;

    useEffect(() => {
        setCellValue(props.cellValue);
    }, [props.cellValue]);
    const renderEditor = () => (
        <div className="editable-cell-editor">
            <Input
                id={`editable-cell-input-${props.id}`}
                invalid={hasError}
                value={cellValue}
                onChange={(event) => {
                    const valid = validateCellValue(event.target.value);
                    setHasError(!valid);
                    setCellValue(event.target.value);
                }}></Input>

            <FontAwesomeIcon
                icon={(props.cancelIcon ?? faXmark) as any}
                size={props.cancelIconSize ?? 'lg'}
                onClick={() => {
                    if (cellValue !== props.cellValue) {
                        setCellValue(props.cellValue);
                    }
                    setEditorState('view');
                }}
            />

            <FontAwesomeIcon
                icon={(props.saveIcon ?? faFloppyDisk) as any}
                size={props.saveIconSize ?? 'lg'}
                onClick={() => {
                    if (!hasError && cellValue !== props.cellValue) {
                        if (props.updateCellValue(cellValue))
                            setEditorState('view');
                    }
                }}
            />

            {props.isDeleteAvailable ? (
                <FontAwesomeIcon
                    className={props.deleteIconClasses}
                    icon={(props.deleteIcon ?? faTrashCan) as any}
                    size={props.deleteIconSize ?? 'lg'}
                    onClick={() => {
                        props?.onDeleteClick();
                    }}
                />
            ) : (
                <></>
            )}
        </div>
    );

    const renderCellContent = () => (
        <div className="editable-cell-view">
            {props.cellViewContent ? (
                <>
                    {' '}
                    {props.id && (
                        <PortalTooltip
                            anchorId={props.id}
                            delayShow={1000}
                            place="top"
                            content={props.cellViewContent}
                        />
                    )}
                    <div onClick={props.onTextClick} id={props.id}>
                        {props.cellViewContent}
                    </div>{' '}
                </>
            ) : (
                <>
                    {' '}
                    {props.id && (
                        <PortalTooltip
                            anchorId={props.id}
                            delayShow={1000}
                            place="top"
                            content={props.cellValue}
                        />
                    )}
                    <div id={props.id}>{props.cellValue}</div>
                </>
            )}

            <FontAwesomeIcon
                icon={(props.editIcon ?? faPenToSquare) as any}
                size={props.editIconSize}
                onClick={() => setEditorState('edit')}
            />

            {props.isDeleteAvailable ? (
                <FontAwesomeIcon
                    className={props.deleteIconClasses}
                    icon={(props.deleteIcon ?? faTrashCan) as any}
                    size={props.deleteIconSize ?? 'lg'}
                    onClick={() => {
                        props?.onDeleteClick();
                    }}
                />
            ) : (
                <></>
            )}
        </div>
    );

    const render = () => {
        switch (editorState) {
            case 'edit':
                return renderEditor();
            default:
                return renderCellContent();
        }
    };

    return <div className="editable-cell-editor">{render()}</div>;
};

export default EditableTableCell;
